import React from 'react';
import { useNavigate } from 'react-router-dom';
import { portfolioData } from '../mock';
import { SketchStar } from '../components/SketchElements';
import { PhotoCard } from '../components/PhotoCard';
import { MaskingTape } from '../components/MaskingTape';

export const HomePage = () => {
  const navigate = useNavigate();
  const { hero, categories } = portfolioData;

  // Photo pile - each card's position offset, rotation, z-index, paper shape
  const cardConfig = [
    { x:   8, y:  18, rot: -12, z: 2, shapeIdx: 0, scale: 1.00 },
    { x:  -4, y:  -8, rot:   7, z: 5, shapeIdx: 1, scale: 1.05 },
    { x:  12, y:  22, rot:  -4, z: 3, shapeIdx: 2, scale: 0.95 },
    { x:  -6, y:  -4, rot:  10, z: 6, shapeIdx: 3, scale: 1.02 },
    { x:   6, y:  10, rot:  -8, z: 4, shapeIdx: 4, scale: 1.08 },
    { x: -10, y:  16, rot:   5, z: 1, shapeIdx: 5, scale: 0.97 },
  ];

  return (
    <div className="min-h-screen pt-24 pb-20 relative overflow-hidden">
      {/* Subtle page-grain texture */}
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.12]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='280' height='280'><filter id='n'><feTurbulence baseFrequency='0.85' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.5 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Notebook-style red stars scattered randomly across the page */}
      {[
        { top: '14%',  left: '4%',   size: 18, rot: -12, delay: 2.6 },
        { top: '22%',  right: '7%',  size: 22, rot:  18, delay: 2.8 },
        { top: '52%',  left: '3%',   size: 16, rot:  -5, delay: 3.0 },
        { top: '46%',  right: '4%',  size: 20, rot:  25, delay: 3.2 },
        { top: '78%',  left: '6%',   size: 24, rot: -15, delay: 3.4 },
        { top: '82%',  right: '8%',  size: 18, rot:  10, delay: 3.6 },
        { top: '10%',  right: '24%', size: 14, rot:   8, delay: 2.5 },
        { top: '88%',  left: '40%',  size: 16, rot: -20, delay: 3.8 },
        { top: '36%',  left: '10%',  size: 12, rot:  15, delay: 2.9 },
      ].map((s, i) => {
        const posStyle = {
          width: `${s.size}px`,
          height: `${s.size}px`,
          transform: `rotate(${s.rot}deg)`,
        };
        if (s.left) posStyle.left = s.left;
        if (s.right) posStyle.right = s.right;
        if (s.top) posStyle.top = s.top;
        return (
          <SketchStar
            key={i}
            className="absolute text-[color:var(--punk-red)]/80 hidden sm:block"
            style={posStyle}
            delay={s.delay}
          />
        );
      })}

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col items-center">
          {/* Profile + tape strip + that's me */}
          <div className="relative">
            <div className="relative animate-pop-in" style={{ animationDelay: '1.0s' }}>
              <div className="absolute -top-3 -left-6 w-24 h-7 tape-red" style={{ transform: 'rotate(-12deg)' }}>
                <span className="font-typewriter text-[10px] text-white absolute inset-0 flex items-center justify-center tracking-widest uppercase">
                  HELLO!
                </span>
              </div>
              <div className="relative w-32 h-32 md:w-36 md:h-36 rounded-full overflow-hidden bg-white/5 ring-4 ring-[color:var(--punk-red)]">
                <img src={hero.profileImage} alt={hero.name} className="w-full h-full object-cover" />
              </div>
            </div>
            <div
              className="absolute left-full top-1/2 -translate-y-1/2 ml-2 md:ml-4 hidden sm:flex items-center gap-1 animate-fade-in"
              style={{ animationDelay: '2.2s', opacity: 0 }}
            >
              <svg viewBox="0 0 70 40" className="w-12 h-8 md:w-16 md:h-10 -rotate-3 text-[color:var(--punk-red)]" fill="none">
                <path d="M64,20 C50,18 36,24 22,20 C16,18 12,17 8,18"
                  stroke="currentColor" strokeWidth="3" strokeLinecap="round"
                  strokeDasharray="80" strokeDashoffset="80"
                  style={{ animation: 'draw-path 0.7s ease-out 2.2s forwards' }} />
                <path d="M16,12 L8,18 L16,26"
                  stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                  strokeDasharray="40" strokeDashoffset="40"
                  style={{ animation: 'draw-path 0.4s ease-out 2.8s forwards' }} />
              </svg>
              <span className="font-marker text-xl uppercase tracking-wide rotate-[-4deg] inline-block whitespace-nowrap text-[color:var(--punk-red)]">
                that's me
              </span>
            </div>
          </div>

          {/* STENCIL NAME with hand-drawn red marker underline */}
          <div className="mt-10 relative animate-fade-in z-30 px-4" style={{ animationDelay: '0.2s', opacity: 0 }}>
            <h1 className="font-stencil text-5xl sm:text-6xl md:text-8xl lg:text-9xl text-white tracking-tight text-center uppercase leading-[0.9] break-words">
              {hero.name}
            </h1>

            {/* Red marker underline — thick, aggressive, hand-drawn, edge-to-edge */}
            <svg
              viewBox="0 0 400 26"
              preserveAspectRatio="none"
              className="absolute left-1/2 -translate-x-1/2 -bottom-2 md:-bottom-3 w-[105%] sm:w-[100%] md:w-[95%] lg:w-[92%] h-5 md:h-7 pointer-events-none"
              fill="none"
              aria-hidden="true"
            >
              {/* Main thick stroke */}
              <path
                d="M2,14 C30,8 70,18 110,11 C150,5 195,18 240,10 C285,4 330,17 370,11 C385,9 395,12 398,11"
                stroke="var(--punk-red)"
                strokeWidth="9"
                strokeLinecap="round"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{ animation: 'draw-path 1.1s cubic-bezier(0.65, 0, 0.35, 1) 1s forwards' }}
              />
              {/* Secondary overlap stroke for marker-bleed feel */}
              <path
                d="M5,17 C32,12 68,21 108,14 C148,9 192,21 238,13 C284,8 328,20 368,14 C384,12 394,15 396,14"
                stroke="var(--punk-red)"
                strokeWidth="5"
                strokeLinecap="round"
                opacity="0.65"
                strokeDasharray="500"
                strokeDashoffset="500"
                style={{ animation: 'draw-path 1.1s cubic-bezier(0.65, 0, 0.35, 1) 1.15s forwards' }}
              />
            </svg>

            {/* Hyper-realistic masking tape on the corner of the title */}
            <MaskingTape
              width={110}
              height={28}
              rotation={14}
              color="cream"
              className="absolute hidden md:block"
              style={{ top: '-12px', right: '-26px' }}
            />
          </div>

          {/* Subtitle */}
          <div className="mt-6 flex items-center flex-wrap justify-center gap-x-3 gap-y-2 animate-fade-in relative z-30 px-4" style={{ animationDelay: '0.6s', opacity: 0 }}>
            <span className="font-typewriter text-xs sm:text-sm md:text-base text-white/70 uppercase tracking-[0.25em]">designer</span>
            <span className="h-px w-6 sm:w-8 bg-[color:var(--punk-red)]" />
            <span className="font-typewriter text-xs sm:text-sm md:text-base text-white/70 uppercase tracking-[0.25em]">illustrator</span>
            <span className="h-px w-6 sm:w-8 bg-[color:var(--punk-red)]" />
            <span className="font-typewriter text-xs sm:text-sm md:text-base text-white uppercase tracking-[0.25em]">storyteller</span>
          </div>

          {/* THE CATEGORIES — single overlapping row */}
          <div className="w-full max-w-[1400px] mx-auto mt-12 md:mt-16">
            {/* Desktop: flex row with subtle overlap */}
            <div className="hidden md:flex items-center justify-center -space-x-2 lg:-space-x-4">
              {categories.map((category, index) => {
                // Landscape 16:9 polaroids to match the natural aspect of the cover images
                const cards = [
                  { w: 248, h: 140, rot: -7, zClass: 'z-[2]' },
                  { w: 256, h: 144, rot:  6, zClass: 'z-[4]' },
                  { w: 240, h: 135, rot: -4, zClass: 'z-[5]' },
                  { w: 264, h: 148, rot:  8, zClass: 'z-[3]' },
                  { w: 252, h: 142, rot: -6, zClass: 'z-[1]' },
                ];
                const c = cards[index];
                return (
                  <div
                    key={category.id}
                    data-testid={`category-circle-${category.id}`}
                    className={`group cursor-pointer animate-drop-in shrink-0 relative ${c.zClass} hover:z-50`}
                    style={{
                      width: `${c.w}px`,
                      height: `${c.h}px`,
                      animationDelay: `${1.9 + index * 0.18}s`,
                      '--rot-start': `${c.rot * 3}deg`,
                      '--rot-end': `0deg`,
                    }}
                    onClick={() => navigate(`/gallery/${category.id}`)}
                  >
                    {/* Rotation wrapper (inline transform) */}
                    <div
                      className="w-full h-full"
                      style={{ transform: `rotate(${c.rot}deg)` }}
                    >
                      {/* Hover translate + scale wrapper (Tailwind transforms) */}
                      <div className="relative w-full h-full transition-transform duration-500 ease-out group-hover:-translate-y-4 group-hover:scale-[1.1]">
                        <PhotoCard
                          src={category.thumbnail}
                          alt={category.title}
                          title={category.title}
                          tapeColor="cream"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Mobile: horizontal scroll-friendly stacked layout */}
            <div className="md:hidden flex flex-col items-center gap-y-7 px-4">
              {categories.map((category, index) => {
                // Landscape 16:9 to match the cover images' natural aspect
                const mobileSizes = [
                  { w: 280, h: 158, rot: -5, align: 'self-start' },
                  { w: 280, h: 158, rot:  5, align: 'self-end' },
                  { w: 280, h: 158, rot: -4, align: 'self-start' },
                  { w: 280, h: 158, rot:  6, align: 'self-end' },
                  { w: 280, h: 158, rot: -7, align: 'self-center' },
                ];
                const m = mobileSizes[index];
                return (
                  <div
                    key={category.id}
                    className={`group cursor-pointer animate-drop-in ${m.align}`}
                    style={{
                      width: `${m.w}px`,
                      height: `${m.h}px`,
                      animationDelay: `${1.9 + index * 0.15}s`,
                      '--rot-start': `${m.rot * 3}deg`,
                      '--rot-end': `0deg`,
                    }}
                    onClick={() => navigate(`/gallery/${category.id}`)}
                  >
                    <div
                      className="relative w-full h-full transition-all duration-500 ease-out"
                      style={{ transform: `rotate(${m.rot}deg)` }}
                    >
                      <PhotoCard
                        src={category.thumbnail}
                        alt={category.title}
                        title={category.title}
                        tapeColor="cream"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer doodle */}
          <div className="mt-20 flex items-center gap-3 animate-fade-in" style={{ animationDelay: '3.6s', opacity: 0 }}>
            <span className="h-px w-10 bg-white/30" />
            <span className="font-typewriter text-xs uppercase tracking-[0.3em] text-white/50">scroll · click · explore</span>
            <span className="h-px w-10 bg-white/30" />
          </div>
        </div>
      </div>
    </div>
  );
};
